import { useEffect } from 'react';

type KeyboardKey = string;
type KeyboardCallback = (event: KeyboardEvent) => void;
type KeyboardCallbacks = Record<KeyboardKey, KeyboardCallback> | KeyboardCallback;

type Handler = {
  id: string;
  callback: (event: KeyboardEvent) => void;
  priority: number;
};

class KeyboardEventManager {
  private handlers: Handler[] = [];

  addHandler(id: string, callback: (event: KeyboardEvent) => void, priority = 0) {
    this.handlers.push({ id, callback, priority });
    this.handlers.sort((a, b) => b.priority - a.priority);
  }

  removeHandler(id: string) {
    this.handlers = this.handlers.filter((handler) => handler.id !== id);
  }

  handleKeyDown = (event: KeyboardEvent) => {
    this.handlers.some((handler) => {
      handler.callback(event);
      return event.defaultPrevented;
    });
  };
}

export const keyboardManager = new KeyboardEventManager();

const useKeyboardAction = (
  callbacks: KeyboardCallbacks,
  keys: KeyboardKey | KeyboardKey[] = 'Escape',
  priority = 0
): void => {
  useEffect(() => {
    const id = Math.random().toString(36);

    const handleKeyDown = (event: KeyboardEvent) => {
      const keysToListen = Array.isArray(keys) ? keys : [keys];
      if (keysToListen.includes(event.key)) {
        if (typeof callbacks === 'function') {
          callbacks(event);
        }
      }
    };

    keyboardManager.addHandler(id, handleKeyDown, priority);
    document.addEventListener('keydown', keyboardManager.handleKeyDown);

    return () => {
      keyboardManager.removeHandler(id);
      document.removeEventListener('keydown', keyboardManager.handleKeyDown);
    };
  }, [keys, callbacks, priority]);
};

export default useKeyboardAction;
